import {
  dateSchema,
  dateTypeSchema,
  emailSchema,
  financingFleetTierSchema,
  Lead,
  leadActionSchema,
  leadDirectionSchema,
  leadFirstTouchKindSchema,
  leadInterestSchema,
  leadLostReasonSchema,
  leadMetaSchema,
  leadPersonSchema,
  leadPrioritySchema,
  leadSourceMetaSchema,
  leadSourceSchema,
  leadStatusSchema,
  nameSchema,
  objectIdSchema,
  paginationWithSearchSchema,
  partySchema,
  phoneSchema,
  priceSchema,
  businessSchema,
  UTagSharedSchema,
} from '@hero/schema';
import { z } from 'zod';
import { languageSchema } from '@hero/schema';
import { API } from '..';

// ─── filter-only schema (the agent's `search_leads` tool composes from this) ──

export const leadsQueryAPISchema = z.object({
  status: leadStatusSchema
    .optional()
    .describe(
      "Filter by the lead's current pipeline status (`new`, `contacted`, `engaged`, `qualified`, `activated`, `won`, `unqualified`, `lost`, `dead`).",
    ),
  business: businessSchema
    .optional()
    .describe(
      "Filter to leads that are in this brand's funnel (`funnels[].business`). A lead can be in several at once — a carrier sold Wathiqa Plus first and Hero later is one lead with two funnels — so this narrows without excluding the other.",
    ),
  statusIn: z
    .array(leadStatusSchema)
    .optional()
    .describe(
      'Filter to leads whose status is ANY of these values (Mongo `$in`). Preferred over `status` when you need a bucket — e.g. the "Filtered out" tab passes `["lost", "unqualified", "dead"]`. Ignored when `status` is also set.',
    ),
  source: leadSourceSchema
    .optional()
    .describe(
      "Filter by `Lead.meta.source` — where the lead came from (`landing_page`, `landing_demo_form`, `sales_hunter`, `referral`, `inbound_call`, `whatsapp`, `marketplace`, `imported`, `other`).",
    ),
  priority: leadPrioritySchema
    .optional()
    .describe("Filter by priority (`hot`, `warm`, `cold`)."),
  interest: leadInterestSchema
    .optional()
    .describe(
      "Filter by an interest present in `Lead.interests[]` (`book_demo`, `request_quote`, `self_signup`, `partnership`, `financing`, `fleet_management`, `generic`, plus every business id — `hero`, `wathiqa_plus` — and every audience product id — `hero_enterprise`, `hero_freelancer`, …). Scalar filter — matches any lead whose interests array contains this value.",
    ),
  direction: leadDirectionSchema
    .optional()
    .describe(
      'Filter by who reached out first: `inbound` — they came to us; `outbound` — we chased them. Derived from `Lead.meta.firstTouch.kind`, so it expands to the matching kinds rather than reading a stored field. Leads captured before first touch was recorded match neither value.',
    ),
  firstTouchKind: leadFirstTouchKindSchema
    .optional()
    .describe(
      'Filter by exactly how the relationship started (`Lead.meta.firstTouch.kind`): `inbound_message`, `outbound_message`, `inbound_call`, `outbound_call`, `self_signup`, `form`. Narrower than `direction` — use it when the distinction between a call and a message matters.',
    ),
  accountKind: z
    .enum(['company', 'freelancer', 'individual'])
    .optional()
    .describe(
      'Filter by what kind of buyer this is: `company`, `freelancer` (owner-operator, bills as their Driver row) or `individual`. Derived from the network roles on `Lead.meta.accountType[]` through `rolesForAccountKind` — there is no stored scalar. Because the mapping is many-to-one, this is a "could be" filter: a lead carrying both `driver` and `broker` matches `freelancer` here but renders as a company.',
    ),
  language: languageSchema
    .optional()
    .describe(
      "Filter by the lead's preferred follow-up language (`ar` / `en` / `ur`) — what an outbound template renders in.",
    ),
  assignedToId: objectIdSchema
    .optional()
    .describe(
      'Filter to leads assigned to this AdminUser (`assignedTo._id`). Resolve from `search_admin_users` when the user names a sales rep.',
    ),
  companyName: nameSchema
    .optional()
    .describe(
      "Filter by `Lead.meta.companyName`. Use the search field for fuzzy matches; this is an exact / prefix filter.",
    ),
  phone: phoneSchema
    .optional()
    .describe(
      'Filter to leads where at least one person on the lead has this phone number (`people[].phone`).',
    ),
  email: emailSchema
    .optional()
    .describe(
      'Filter to leads where at least one person on the lead has this email (`people[].email`).',
    ),
  simpleDate: dateTypeSchema
    .optional()
    .describe(
      "Window filter on `doc.createdAt`: 'today', 'this_week', 'this_month', etc. Mirrors the `simpleDate` filter on orders.",
    ),
  createdFrom: dateSchema
    .optional()
    .describe('Filter to leads created on or after this date (`doc.createdAt >=`).'),
  createdTo: dateSchema
    .optional()
    .describe('Filter to leads created on or before this date (`doc.createdAt <=`).'),
  nextFollowUpBefore: dateSchema
    .optional()
    .describe(
      'Filter to leads with a `nextFollowUpDate` on or before this date — i.e. due-today / overdue leads.',
    ),
  nextFollowUpAfter: dateSchema
    .optional()
    .describe(
      'Filter to leads with a `nextFollowUpDate` on or after this date. Combine with `nextFollowUpBefore` for a window — the "Follow-up today" tab passes `startOfToday` + `endOfToday`.',
    ),
  tags: z
    .array(z.string())
    .optional()
    .describe('Filter to leads whose `UTags[].code` matches any value in this list.'),
});
export type LeadsQueryAPI = z.infer<typeof leadsQueryAPISchema>;

// ─── get-all (admin) ──────────────────────────────────────────────────────────

export const leadGetAllAdminAPISchema = paginationWithSearchSchema.extend({
  ...leadsQueryAPISchema.shape,
});
export type LeadGetAllAdminAPI = API<z.infer<typeof leadGetAllAdminAPISchema>, Lead[]>;

// ─── get-one ──────────────────────────────────────────────────────────────────

export const leadGetOneAPISchema = z.object({
  _id: objectIdSchema.describe('`Lead._id` (24-char Mongo ObjectId).'),
});
export type LeadGetOneAPI = API<z.infer<typeof leadGetOneAPISchema>, Lead>;

// ─── admin create ─────────────────────────────────────────────────────────────

/**
 * Admin / sales hunter create. `meta.source` is required (defaults to
 * `'sales_hunter'` in the admin UI). At least one of
 * `meta.companyName` / any `people[].phone` / any `people[].email` is
 * required — validated SERVER-SIDE (not in Zod) so a hunter can submit
 * "just a phone" without the schema rejecting it.
 */
export const leadCreateAdminAPISchema = z.object({
  title: nameSchema
    .optional()
    .describe(
      'Optional display title. When omitted the backend derives it from `meta.companyName ?? people[0].name ?? people[0].phone`.',
    ),
  meta: leadMetaSchema.describe(
    'Company-level metadata + origin (`meta.source` is required; e.g. `sales_hunter`).',
  ),
  people: z
    .array(leadPersonSchema)
    .default([])
    .describe(
      'People attached to this lead. Each entry is `{ name?, phone?, email?, position?, isPrimary?, notes? }`. The admin can submit one or many at once.',
    ),
  status: leadStatusSchema
    .default('new')
    .describe('Initial pipeline status; defaults to `new`.'),
  interests: z
    .array(leadInterestSchema)
    .optional()
    .describe(
      'What the lead wants — multi-select (book demo / quote / partnership / financing / fleet management / ...). Omit or pass `[]` when no specific intent is captured.',
    ),
  priority: leadPrioritySchema.optional().describe('Initial priority (`hot` / `warm` / `cold`).'),
  language: languageSchema.optional().describe('Preferred follow-up language (`ar` / `en` / `ur`).'),
  assignedToId: objectIdSchema
    .optional()
    .describe(
      'AdminUser `_id` to own this lead. The backend snapshots the user into `Lead.assignedTo`.',
    ),
  estimatedValue: priceSchema
    .optional()
    .describe('Rough commercial value, used for pipeline-value analytics.'),
  nextFollowUpDate: dateSchema
    .optional()
    .describe('When the sales rep should follow up next.'),
  org: partySchema
    .optional()
    .describe(
      'Existing Org snapshot — set when the lead is an upsell on an existing customer. Stored as the first entry of `Lead.parties[]`; it does not mark the lead converted, which is `Lead.converted`.',
    ),
  UTags: z
    .array(UTagSharedSchema)
    .optional()
    .describe('Universal tag snapshots (`{ _id, name, code }`) attached to this lead.'),
});
export type LeadCreateAdminAPI = API<z.infer<typeof leadCreateAdminAPISchema>, Lead>;

// ─── update (admin) ───────────────────────────────────────────────────────────

export const leadUpdateAPISchema = z.object({
  _id: objectIdSchema.describe('`Lead._id` to update.'),
  title: nameSchema.optional(),
  meta: leadMetaSchema.partial().optional().describe('Patch for `Lead.meta` (shallow).'),
  status: leadStatusSchema.optional(),
  interests: z
    .array(leadInterestSchema)
    .optional()
    .describe('Replace the interests array. Pass `[]` to clear.'),
  priority: leadPrioritySchema.optional(),
  language: languageSchema.optional(),
  estimatedValue: priceSchema.optional(),
  nextFollowUpDate: dateSchema.optional(),
  wonDate: dateSchema
    .optional()
    .describe(
      'Override / backdate the conversion date. Normally auto-stamped to "now" when `status` transitions into `won` (including via `convertLink`); pass an explicit value only when recording a deal that closed off-platform.',
    ),
  lostDate: dateSchema
    .optional()
    .describe(
      'Override / backdate the loss date. Normally auto-stamped to "now" when `status` transitions into a terminal-lost value (`lost` / `unqualified` / `dead`); transitions within the terminal bucket do NOT refresh the date.',
    ),
  lostReason: leadLostReasonSchema
    .optional()
    .describe(
      'Required when flipping `status` to a terminal value (`lost` / `unqualified` / `dead`).',
    ),
  UTags: z.array(UTagSharedSchema).optional(),
});
export type LeadUpdateAPI = API<z.infer<typeof leadUpdateAPISchema>, Lead>;

// ─── delete ───────────────────────────────────────────────────────────────────

export const leadDeleteAPISchema = z.object({
  _id: objectIdSchema.describe('`Lead._id` to delete.'),
});
export type LeadDeleteAPI = API<z.infer<typeof leadDeleteAPISchema>, Lead>;

// ─── people sub-edits ─────────────────────────────────────────────────────────

export const leadAddPersonAPISchema = z.object({
  _id: objectIdSchema.describe('`Lead._id` to mutate.'),
  person: leadPersonSchema.describe(
    'Person to append to `Lead.people[]`. If `isPrimary` is set, the existing primary is demoted.',
  ),
});
export type LeadAddPersonAPI = API<z.infer<typeof leadAddPersonAPISchema>, Lead>;

export const leadUpdatePersonAPISchema = z.object({
  _id: objectIdSchema.describe('`Lead._id` to mutate.'),
  index: z
    .number()
    .int()
    .min(0)
    .describe('Zero-based index of the `people[]` entry to update.'),
  person: leadPersonSchema.describe('New value for `people[index]`.'),
});
export type LeadUpdatePersonAPI = API<z.infer<typeof leadUpdatePersonAPISchema>, Lead>;

export const leadRemovePersonAPISchema = z.object({
  _id: objectIdSchema.describe('`Lead._id` to mutate.'),
  index: z
    .number()
    .int()
    .min(0)
    .describe('Zero-based index of the `people[]` entry to remove.'),
});
export type LeadRemovePersonAPI = API<z.infer<typeof leadRemovePersonAPISchema>, Lead>;

// ─── actions ──────────────────────────────────────────────────────────────────

export const leadAddActionAPISchema = z.object({
  _id: objectIdSchema.describe('`Lead._id` to attach the action to.'),
  action: leadActionSchema.omit({ by: true }).describe(
    'Logged action. The admin Logs composer writes `type: note`; older rows may be a call / email / visit / meeting. `by` is stamped by the backend from the session.',
  ),
});
export type LeadAddActionAPI = API<z.infer<typeof leadAddActionAPISchema>, Lead>;

export const leadUpdateActionAPISchema = z.object({
  _id: objectIdSchema.describe('`Lead._id`.'),
  index: z.number().int().min(0).describe('Zero-based index of the action to update.'),
  action: leadActionSchema.omit({ by: true }).describe(
    'New value for `actions[index]`. `by` is preserved from the original row.',
  ),
});
export type LeadUpdateActionAPI = API<z.infer<typeof leadUpdateActionAPISchema>, Lead>;

// ─── assignment ───────────────────────────────────────────────────────────────

export const leadAssignAPISchema = z.object({
  _id: objectIdSchema.describe('`Lead._id` to assign.'),
  assignedToId: objectIdSchema
    .optional()
    .describe(
      'AdminUser `_id` to assign this lead to. Pass `null`/omit to unassign.',
    ),
});
export type LeadAssignAPI = API<z.infer<typeof leadAssignAPISchema>, Lead>;

// ─── manual conversion link ───────────────────────────────────────────────────

export const leadConvertLinkAPISchema = z.object({
  _id: objectIdSchema.describe('`Lead._id` to convert.'),
  converted: partySchema.describe(
    'The account this lead became — any `Party`, so an Org, the User behind an individual customer, or the Driver row a freelancer bills as. Picked through the admin entity-directory selector; the backend flips `status` to `won`, appends the party to `Lead.parties[]`, and writes a `converted_link` timeline row.',
  ),
});
export type LeadConvertLinkAPI = API<z.infer<typeof leadConvertLinkAPISchema>, Lead>;

// ─── PUBLIC landing-form create (no auth) ─────────────────────────────────────

/**
 * Landing-page intake. Intentionally FLAT so the landing form sends
 * `{ personName, phone, email, ... }` not nested `people[]`. The
 * backend (`LeadService.createFromPublic`) wraps the flat fields into
 * `people: [{ name: personName, phone, email, isPrimary: true }]` and
 * builds `meta = { companyName?, fleetSize?, description?, source:
 * 'landing_page', sourceMeta: { ...client, ip, userAgent } }`. Status
 * is stamped `'new'`. `interests` defaults to `[]` — the landing page
 * is multi-purpose (demo, contact, financing, ...) and the sales team
 * qualifies the interest during the follow-up. IP and User-Agent in
 * `sourceMeta` are stamped server-side (any values the client passes
 * for them are dropped).
 *
 * `nextFollowUpDate` and the initial `form_submitted` action are
 * auto-stamped by the backend — the client does not supply them.
 */
export const leadPublicCreateAPISchema = z.object({
  personName: nameSchema
    .optional()
    .describe("Submitter's full name. Optional — the form may collect only a phone."),
  phone: phoneSchema
    .optional()
    .describe("Submitter's phone number (E.164 preferred)."),
  email: emailSchema.optional().describe("Submitter's email address."),
  companyName: nameSchema
    .optional()
    .describe('Name of the company the submitter represents.'),
  fleetSize: financingFleetTierSchema
    .optional()
    .describe(
      'Fleet-size bracket (`lt_10`, `11_20`, ..., `gt_500`) — reuses the financing profile enum so the same answer is only ever asked once. Optional on the API; the landing form makes it required client-side.',
    ),
  description: z
    .string()
    .optional()
    .describe(
      "Free-form description of the submitter's business and needs. Captured so the sales team has context before the first call. Trimmed; empty strings are treated as omitted.",
    ),
  interests: z
    .array(leadInterestSchema)
    .optional()
    .describe(
      'What the submitter wants — multi-select. Defaults to `[]` server-side when omitted (the landing page is multi-purpose; the sales team qualifies the intent on first contact).',
    ),
  language: languageSchema
    .optional()
    .describe("Preferred follow-up language (`ar` / `en`)."),
  sourceMeta: leadSourceMetaSchema
    .omit({ ip: true, userAgent: true, referral: true })
    .optional()
    .describe(
      'Client-supplied attribution metadata (UTM, referrer, landingPath, formId, `referralCode` from `?r=` / the `hero_r` cookie, `referralVisitorId` from the `hero_vid` cookie). The backend stamps `ip` and `userAgent` on top of this and resolves `referralCode` into `sourceMeta.referral`; the resolved `referral` object itself is never accepted from the client.',
    ),
});
export type LeadPublicCreateAPI = API<z.infer<typeof leadPublicCreateAPISchema>, { _id: string }>;

// ─── analytics ────────────────────────────────────────────────────────────────

export const analyticsLeadsAPISchema = z.object({
  dateType: dateTypeSchema
    .default('this_month')
    .describe("Window for the analytics rollup. Defaults to 'this_month'."),
  source: leadSourceSchema
    .optional()
    .describe('Scope the analytics to a single source (`landing_demo_form`, `sales_hunter`, ...).'),
  assignedToId: objectIdSchema
    .optional()
    .describe('Scope to leads assigned to a single AdminUser.'),
});
export type AnalyticsLeadsQuery = z.infer<typeof analyticsLeadsAPISchema>;

/**
 * Response payload for `getLeadsAnalytics`.
 */
export type AnalyticsLeadsData = {
  /** Total leads in the window (matching the optional filters). */
  total: number;
  /** Leads created in the window grouped by `status`. */
  byStatus: Record<z.infer<typeof leadStatusSchema>, number>;
  /** Leads created in the window grouped by `meta.source`. */
  bySource: Record<z.infer<typeof leadSourceSchema>, number>;
  /** Leads grouped by assignee. Includes an `unassigned` bucket. */
  byAssignee: Array<{
    assigneeId: string | 'unassigned';
    name: string;
    count: number;
  }>;
  /** Conversion funnel — counts per logical pipeline stage. */
  funnel: {
    new: number;
    contacted: number;
    qualified: number;
    won: number;
    lostOrDead: number;
    conversionRate: number;
  };
  /** Average hours from creation to first `contacted` status. `null` when no leads. */
  averageHoursToContact: number | null;
  /** Sum of `estimatedValue` per status (pipeline value). */
  pipelineValueByStatus: Record<z.infer<typeof leadStatusSchema>, number>;
};

export type AnalyticsLeadsAPI = API<AnalyticsLeadsQuery, AnalyticsLeadsData>;
